import { supabase } from '../../lib/supabaseClient';
import {
  isUniversalSearchReady,
  UNIVERSAL_SEARCH_LIMIT_PER_KIND,
  type CalendarSearchKind,
  type UniversalSearchHit,
} from '../../lib/universalSearch';
import { asCollaboratorRole, ROLE_LABELS } from '../auth/permissions';
import { fcDateToYmd } from '../calendar/calendarUrlState';
import {
  CONTACT_CATEGORY_LABELS,
  getContactDisplayName,
  type Contact,
  type ContactCategory,
} from '../contacts/contactService';

export interface UniversalSearchScope {
  dossiers: boolean;
  contacts: boolean;
  tickets: boolean;
  files: boolean;
  emails: boolean;
  calendar: boolean;
  /** Suivi de la prescription activé au cabinet. */
  prescription: boolean;
  tasks: boolean;
  collaborators: boolean;
}

type DossierRow = {
  id: string;
  title: string;
  number: string | null;
  type_affaire: string | null;
  stage: string | null;
  archived_at: string | null;
  closed_at: string | null;
};

type ContactRow = Pick<
  Contact,
  'id' | 'kind' | 'first_name' | 'last_name' | 'company_name' | 'email' | 'siret' | 'contact_category'
>;

type TicketRow = {
  id: string;
  sender_name: string | null;
  sender_email: string | null;
  message: string;
};

type FileRow = {
  id: string;
  filename: string;
  dossier_id: string;
};

type TaskRow = {
  id: string;
  content: string;
  dossier_id: string | null;
};

type CalendarRow = {
  id: string;
  title: string;
  number: string | null;
  next_hearing_at: string | null;
  prescription_date: string | null;
};

type GraftEmailRow = {
  id: string;
  dossier_id: string | null;
  subject: string | null;
  from_email: string;
  sent_at: string | null;
};

type GmailEmailRow = {
  id: string;
  dossier_id: string;
  subject: string | null;
  from_email: string;
  from_name: string | null;
  sent_at: string;
};

type CollaboratorRow = {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_partner: boolean;
};

type DossierLabelRow = {
  id: string;
  title: string | null;
  number: string | null;
};

function escapeIlike(term: string): string {
  return term.replace(/\\/g, '\\\\').replace(/[%_]/g, '\\$&');
}

function sanitizeOrIlike(term: string): string {
  return escapeIlike(term).replace(/[,()]/g, ' ');
}

function dossierLabel(row: DossierLabelRow | undefined): string | undefined {
  if (!row) return undefined;
  const parts = [row.number, row.title].filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(' · ') : undefined;
}

async function labelsForDossierIds(ids: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return new Map();
  const { data, error } = await supabase
    .from('dossiers')
    .select('id, title, number')
    .in('id', unique);
  if (error) throw new Error(`Failed to load dossier labels: ${error.message}`);
  const map = new Map<string, string>();
  for (const row of (data ?? []) as DossierLabelRow[]) {
    const label = dossierLabel(row);
    if (label) map.set(row.id, label);
  }
  return map;
}

async function searchDossiers(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = sanitizeOrIlike(term);
  const { data, error } = await supabase
    .from('dossiers')
    .select('id, title, number, type_affaire, stage, archived_at, closed_at')
    .is('deleted_at', null)
    .or(`title.ilike.%${s}%,number.ilike.%${s}%`)
    .order('opened_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`Failed to search dossiers: ${error.message}`);
  return ((data ?? []) as DossierRow[]).map((row) => ({
    kind: 'dossier',
    id: row.id,
    title: row.title,
    subtitle: row.number ?? undefined,
    dossierType: row.type_affaire ?? undefined,
    dossierStage: row.stage ?? undefined,
    archivedAt: row.archived_at,
    closedAt: row.closed_at,
  }));
}

async function searchContacts(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = sanitizeOrIlike(term);
  const { data, error } = await supabase
    .from('contacts')
    .select('id, kind, first_name, last_name, company_name, email, siret, contact_category')
    .or(
      [
        `first_name.ilike.%${s}%`,
        `last_name.ilike.%${s}%`,
        `company_name.ilike.%${s}%`,
        `email.ilike.%${s}%`,
        `siret.ilike.%${s}%`,
      ].join(','),
    )
    .order('last_name', { ascending: true, nullsFirst: false })
    .limit(limit);
  if (error) throw new Error(`Failed to search contacts: ${error.message}`);
  return ((data ?? []) as ContactRow[]).map((row) => {
    const category = row.contact_category as ContactCategory | null;
    return {
      kind: 'contact',
      id: row.id,
      title: getContactDisplayName(row as Contact),
      subtitle: row.email ?? undefined,
      contactCategoryLabel: category ? CONTACT_CATEGORY_LABELS[category] : null,
    };
  });
}

function emailOrFilter(s: string, extra: string[] = []): string {
  const parts = [`subject.ilike.%${s}%`, `from_email.ilike.%${s}%`, ...extra];
  if (s.includes('@') && !/[,{}"]/.test(s)) {
    parts.push(`to_emails.cs.{"${s}"}`);
  }
  return parts.join(',');
}

function emailHit(
  source: 'graft' | 'gmail',
  row: { id: string; dossier_id: string | null; subject: string | null; from_email: string; from_name?: string | null },
  dossierLabel: string | undefined,
): UniversalSearchHit {
  const from = row.from_name?.trim() || row.from_email;
  return {
    kind: 'email',
    id: `${source}:${row.id}`,
    title: row.subject?.trim() || '(sans objet)',
    subtitle: [from, dossierLabel].filter(Boolean).join(' · '),
    dossierId: row.dossier_id,
  };
}

async function searchEmails(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = sanitizeOrIlike(term);
  const [graftRes, gmailRes] = await Promise.all([
    supabase
      .from('dossier_emails')
      .select('id, dossier_id, subject, from_email, sent_at')
      .eq('status', 'grafted')
      .not('dossier_id', 'is', null)
      .or(emailOrFilter(s))
      .order('sent_at', { ascending: false, nullsFirst: false })
      .limit(limit),
    supabase
      .from('gmail_archived_emails')
      .select('id, dossier_id, subject, from_email, from_name, sent_at')
      .or(emailOrFilter(s, [`from_name.ilike.%${s}%`]))
      .order('sent_at', { ascending: false })
      .limit(limit),
  ]);
  if (graftRes.error) throw new Error(`Failed to search emails: ${graftRes.error.message}`);
  if (gmailRes.error) throw new Error(`Failed to search emails: ${gmailRes.error.message}`);

  const graftRows = (graftRes.data ?? []) as GraftEmailRow[];
  const gmailRows = (gmailRes.data ?? []) as GmailEmailRow[];
  const labels = await labelsForDossierIds([
    ...graftRows.map((row) => row.dossier_id).filter((id): id is string => Boolean(id)),
    ...gmailRows.map((row) => row.dossier_id),
  ]);

  const merged: Array<{ sentAt: string; hit: UniversalSearchHit }> = [
    ...graftRows
      .filter((row) => row.dossier_id)
      .map((row) => ({
        sentAt: row.sent_at ?? '',
        hit: emailHit('graft', row, labels.get(row.dossier_id!)),
      })),
    ...gmailRows.map((row) => ({
      sentAt: row.sent_at,
      hit: emailHit('gmail', { ...row, from_name: row.from_name }, labels.get(row.dossier_id)),
    })),
  ];
  merged.sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  return merged.slice(0, limit).map((row) => row.hit);
}

function collaboratorRoleLabel(row: CollaboratorRow): string {
  if (row.is_partner) return 'Associé';
  return ROLE_LABELS[asCollaboratorRole(row.role) ?? 'lawyer'] ?? row.role;
}

async function searchCollaborators(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = sanitizeOrIlike(term);
  const { data, error } = await supabase
    .from('collaborators')
    .select('id, full_name, email, role, is_partner')
    .eq('is_active', true)
    .neq('role', 'client_portal')
    .or(`full_name.ilike.%${s}%,email.ilike.%${s}%`)
    .order('full_name', { ascending: true })
    .limit(limit);
  if (error) throw new Error(`Failed to search collaborators: ${error.message}`);
  return ((data ?? []) as CollaboratorRow[]).map((row) => ({
    kind: 'collaborator',
    id: row.id,
    title: row.full_name,
    subtitle: [collaboratorRoleLabel(row), row.email].filter(Boolean).join(' · '),
  }));
}

async function searchTickets(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = sanitizeOrIlike(term);
  const { data, error } = await supabase
    .from('tickets')
    .select('id, sender_name, sender_email, message')
    .or(`sender_name.ilike.%${s}%,sender_email.ilike.%${s}%,message.ilike.%${s}%`)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`Failed to search tickets: ${error.message}`);
  return ((data ?? []) as TicketRow[]).map((row) => ({
    kind: 'ticket',
    id: row.id,
    title: row.sender_name?.trim() || row.sender_email || 'Demande',
    subtitle: [row.sender_email, row.message.replace(/\s+/g, ' ').trim().slice(0, 80)]
      .filter(Boolean)
      .join(' · '),
  }));
}

// Documents : par nom de fichier seulement, jamais par contenu (décision cabinet).
async function searchFiles(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = escapeIlike(term);
  const { data, error } = await supabase
    .from('dossier_files')
    .select('id, filename, dossier_id')
    .ilike('filename', `%${s}%`)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`Failed to search files: ${error.message}`);
  const rows = (data ?? []) as FileRow[];
  const labels = await labelsForDossierIds(rows.map((row) => row.dossier_id));
  return rows.map((row) => ({
    kind: 'file',
    id: row.id,
    title: row.filename,
    subtitle: labels.get(row.dossier_id),
    dossierId: row.dossier_id,
  }));
}

function toCalendarDateParam(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return fcDateToYmd(d);
}

function formatSearchDateFr(value: string): string {
  const ymd = toCalendarDateParam(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function calendarHit(
  row: CalendarRow,
  calendarKind: CalendarSearchKind,
  dateValue: string,
): UniversalSearchHit {
  const ymd = toCalendarDateParam(dateValue);
  const kindLabel = calendarKind === 'hearing' ? 'Audience' : 'Prescription';
  const parts = [row.number, kindLabel, formatSearchDateFr(ymd)].filter(Boolean);
  return {
    kind: 'calendar',
    id: `${calendarKind}:${row.id}`,
    title: row.title,
    subtitle: parts.join(' · '),
    dossierId: row.id,
    calendarDate: ymd,
    calendarKind,
  };
}

async function searchDossiersForCalendar(
  term: string,
  column: 'next_hearing_at' | 'prescription_date',
  limit: number,
): Promise<CalendarRow[]> {
  const s = sanitizeOrIlike(term);
  const { data, error } = await supabase
    .from('dossiers')
    .select('id, title, number, next_hearing_at, prescription_date')
    .is('deleted_at', null)
    .not(column, 'is', null)
    .or(`title.ilike.%${s}%,number.ilike.%${s}%`)
    .order(column, { ascending: true })
    .limit(limit);
  if (error) throw new Error(`Failed to search calendar: ${error.message}`);
  return (data ?? []) as CalendarRow[];
}

async function searchCalendar(
  term: string,
  withPrescription: boolean,
  limit: number,
): Promise<UniversalSearchHit[]> {
  const [hearings, prescriptions] = await Promise.all([
    searchDossiersForCalendar(term, 'next_hearing_at', limit),
    withPrescription
      ? searchDossiersForCalendar(term, 'prescription_date', limit)
      : Promise.resolve([]),
  ]);
  const hits: UniversalSearchHit[] = [];
  for (const row of hearings) {
    if (row.next_hearing_at) hits.push(calendarHit(row, 'hearing', row.next_hearing_at));
  }
  for (const row of prescriptions) {
    if (row.prescription_date) hits.push(calendarHit(row, 'prescription', row.prescription_date));
  }
  return hits.slice(0, limit);
}

async function searchTasks(term: string, limit: number): Promise<UniversalSearchHit[]> {
  const s = escapeIlike(term);
  const { data, error } = await supabase
    .from('tasks')
    .select('id, content, dossier_id')
    .eq('is_deleted', false)
    .eq('is_completed', false)
    .ilike('content', `%${s}%`)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`Failed to search tasks: ${error.message}`);
  const rows = (data ?? []) as TaskRow[];
  const labels = await labelsForDossierIds(
    rows.map((row) => row.dossier_id).filter((id): id is string => Boolean(id)),
  );
  return rows.map((row) => ({
    kind: 'task',
    id: row.id,
    title: row.content,
    subtitle: row.dossier_id ? labels.get(row.dossier_id) : 'Sans dossier',
    dossierId: row.dossier_id,
  }));
}

async function settleHits(factory: () => Promise<UniversalSearchHit[]>): Promise<UniversalSearchHit[]> {
  try {
    return await factory();
  } catch {
    return [];
  }
}

export async function searchUniversal(
  query: string,
  scope: UniversalSearchScope,
  limit: number = UNIVERSAL_SEARCH_LIMIT_PER_KIND,
): Promise<UniversalSearchHit[]> {
  if (!isUniversalSearchReady(query)) return [];
  const term = query.trim();

  const batches = await Promise.all([
    scope.dossiers ? settleHits(() => searchDossiers(term, limit)) : Promise.resolve([]),
    scope.contacts ? settleHits(() => searchContacts(term, limit)) : Promise.resolve([]),
    scope.tickets ? settleHits(() => searchTickets(term, limit)) : Promise.resolve([]),
    scope.files ? settleHits(() => searchFiles(term, limit)) : Promise.resolve([]),
    scope.emails ? settleHits(() => searchEmails(term, limit)) : Promise.resolve([]),
    scope.calendar ? settleHits(() => searchCalendar(term, scope.prescription, limit)) : Promise.resolve([]),
    scope.tasks ? settleHits(() => searchTasks(term, limit)) : Promise.resolve([]),
    scope.collaborators ? settleHits(() => searchCollaborators(term, limit)) : Promise.resolve([]),
  ]);

  return batches.flat();
}
